import { dsvFormat } from 'd3';
import React, { useContext, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { Trans, useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import {
  CoordinateSystemModel,
  GeoidModel,
} from '../../../../../../typings/api/skymap/rest/v1/.common';
import { isValidNumber } from '../../../../utils/math/number';
import { isDefined } from '../../../../utils/variables';
import { CoordinateManager } from '../../../../viewer/coordinate-manager';
import { Coordinates } from '../../../../viewer/utilities/math-helpers';
import { GeodataSetupContext } from '../../../state/geodata-setup-state';
import { ProjectSpatialReferenceContext } from '../../../state/project-reference-state';
import { DataTable, DataTableColumns, DataTableSortType } from '../../data-table/data-table';
import { FileSelectionButton } from '../../file-selection-button/file-selection-button';
import { Icon } from '../../icon/icon';
import { IconPanel } from '../../icon-panel/icon-panel';
import { InfoBox } from '../../info-box/info-box';
import { Stack } from '../../stack/stack';
import { GcpRow, isInvalidGcpRow } from '../gcp-parser';
import {
  GcpColumnMapping,
  GcpColumnMapping as GcpColumnMappingComponent,
} from './gcp-column-mapping';

function parseCsv(content: string, options = { minColumns: 1 }) {
  const delimiters = ['\t', ';', ','];

  function parseUniformCsv(delimiter: string) {
    try {
      let result = dsvFormat(delimiter).parseRows(content);
      result = result.filter((x) => x.length >= result[0].length);

      if (result.length === 0) {
        return undefined;
      }

      const columnCount = result[0].length;

      // There needs to be at least 3 columns (northing, heighting and height)
      if (columnCount < options.minColumns) {
        return undefined;
      }

      // If there is only 1 column, ensure that this is the last delimiter to ensure all delimiters
      // were tested.
      if (columnCount === 1 && delimiter !== delimiters.at(-1)) {
        return undefined;
      }

      // The result needs to be uniform to be valid. (i.e. all rows have the same number of columns)
      if (!result.every((x) => x.length >= columnCount)) {
        return undefined;
      }

      return result;
    } catch {
      return undefined;
    }
  }

  for (const delimiter of delimiters) {
    const result = parseUniformCsv(delimiter);

    if (isDefined(result)) {
      return result;
    }
  }

  throw new Error(
    `Failed parsing CSV. The file needs to be delimited by either tab, semicolon or comma. And every row needs to have the same amount of columns.`,
  );
}

function parseCsvRowCoordinate(
  row: string[],
  northingRow: number,
  eastingRow: number,
  heightRow: number,
  coordinateSystem: CoordinateSystemModel,
  geoid?: GeoidModel,
): Coordinates {
  const coordinate: Coordinates = {
    northing: Number(row[northingRow]?.replace(',', '.').replaceAll(' ', '')),
    easting: Number(row[eastingRow]?.replace(',', '.').replaceAll(' ', '')),
    height: Number(row[heightRow]?.replace(',', '.').replaceAll(' ', '')),
  };

  if (
    isValidNumber(coordinate.height) &&
    CoordinateManager.getInstance().isValidCoordinate(coordinate, {
      proj4: coordinateSystem.proj4,
      geoidBounds: geoid?.header,
    })
  ) {
    return coordinate;
  }

  throw new Error('Error parsing CSV row.');
}

type GcpFileImporterProps = {
  onImport: (data: { filename: string; gcpPoints: GcpRow[] }) => void;
  initialData?: { filename: string; gcpPoints: GcpRow[] };
  onDataChange?: (data: { filename: string; gcpPoints: GcpRow[] }) => void;
};

export const GcpFileImporter = ({ onImport, initialData, onDataChange }: GcpFileImporterProps) => {
  const { coordinateSystem, geoid } = useContext(ProjectSpatialReferenceContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const [selectedFile, setSelectedFile] = React.useState<File | null>(
    initialData ? new File([], initialData.filename) : null,
  );
  const [fileContent, setFileContent] = React.useState<string | null>(null);
  const [csvColumns, setCsvColumns] = React.useState<string[]>([]);
  const [showColumnMapping, setShowColumnMapping] = React.useState(false);
  const [isImported, setIsImported] = React.useState(!!initialData);
  const [showInvalidCoordinatesWarning, setShowInvalidCoordinatesWarning] = React.useState(false);
  const [showDuplicateNamesWarning, setShowDuplicateNamesWarning] = React.useState(false);
  const projectionRange = useMemo(() => {
    return CoordinateManager.getInstance().getProjectionRange({
      proj4: coordinateSystem?.proj4,
      geoidBounds: geoid?.header,
    });
  }, [coordinateSystem, geoid]);
  const [columnMapping, setColumnMapping] = React.useState<GcpColumnMapping>({
    name: 0,
    northing: 1,
    easting: 2,
    height: 3,
  });
  const [error, setError] = React.useState<string | null>(null);
  const [validRowCount, setValidRowCount] = React.useState<number | null>(
    initialData ? initialData.gcpPoints.length : null,
  );
  const [validatedRows, setValidatedRows] = React.useState<GcpRow[]>(initialData?.gcpPoints || []);
  // Track accumulated points from all imported files
  const [accumulatedPoints, setAccumulatedPoints] = React.useState<GcpRow[]>(
    initialData?.gcpPoints || [],
  );
  const context = useContext(GeodataSetupContext);

  useEffect(() => {
    if (!isDefined(context)) {
      return;
    }
    context.setIsImportingGcpFile(showColumnMapping);
    return () => {
      context.setIsImportingGcpFile(false);
    };
  }, [context, showColumnMapping]);

  // dragCount holds number of elements we have currently dragged into.
  // As long as dragCounts > 0 we are dragging over the droppable area.
  const dragCount = React.useRef(0);
  const [isDropable, setIsDropable] = React.useState(false);

  // Reset drag state when drag ends (including when cancelled)
  useEffect(() => {
    const handleDragEnd = () => {
      dragCount.current = 0;
      setIsDropable(false);
    };

    // Listen for dragend on both document and window to catch all cases
    document.addEventListener('dragend', handleDragEnd);
    window.addEventListener('dragend', handleDragEnd);

    // Also listen for dragleave on document to catch when leaving the page entirely
    const handleDocumentDragLeave = (e: DragEvent) => {
      // Only reset if we're leaving the document entirely (relatedTarget is null or outside)
      if (!e.relatedTarget || (e.relatedTarget as Node).nodeType === Node.DOCUMENT_NODE) {
        dragCount.current = 0;
        setIsDropable(false);
      }
    };

    document.addEventListener('dragleave', handleDocumentDragLeave);

    return () => {
      document.removeEventListener('dragend', handleDragEnd);
      window.removeEventListener('dragend', handleDragEnd);
      document.removeEventListener('dragleave', handleDocumentDragLeave);
    };
  }, []);

  const handleFileSelected = React.useCallback(
    async (file: File) => {
      setError(null);
      setSelectedFile(file);
      setShowColumnMapping(true);
      // Clear only the current file's validated rows (for column mapping preview)
      // But keep accumulatedPoints intact
      setValidatedRows([]);
      setValidRowCount(null);
      setShowDuplicateNamesWarning(false);
      setShowInvalidCoordinatesWarning(false);

      try {
        const content = await file.text();
        setFileContent(content);

        // Parse CSV to detect columns
        const rows = parseCsv(content, { minColumns: 1 });

        if (rows.length === 0) {
          setError(t('geodataSetup.uploadGcp.noColumnsDetected', { ns: 'cloudProcessing' }));
          setSelectedFile(null);
          return;
        }

        // Use first row as column names
        const columns = rows[0].map((col, index) => col.trim() || `Column ${index + 1}`);
        setCsvColumns(columns);

        // Set default mapping (first 4 columns)
        setColumnMapping({
          name: 0,
          northing: Math.min(1, columns.length - 1),
          easting: Math.min(2, columns.length - 1),
          height: Math.min(3, columns.length - 1),
        });

        // Show column mapping UI
        setShowColumnMapping(true);
      } catch (err) {
        console.error('Failed to read GCP file:', err);
        setError(
          err instanceof Error
            ? err.message
            : t('geodataSetup.uploadGcp.failedToRead', { ns: 'cloudProcessing' }),
        );
        setSelectedFile(null);
        setFileContent(null);
        setCsvColumns([]);
      }
    },
    [t],
  );

  // Update validated rows when initialData changes (e.g. loading from context when returning to step).
  // Do not overwrite when the user has already selected a file (selectedFile with a name) so we
  // don't clear their choice before they click Import.
  useEffect(() => {
    if (initialData && (!selectedFile || !selectedFile.name)) {
      setValidatedRows(initialData.gcpPoints);
      setValidRowCount(initialData.gcpPoints.length);
      setAccumulatedPoints(initialData.gcpPoints);
      setSelectedFile(new File([], initialData.filename));
      setIsImported(true);
    }
  }, [initialData, selectedFile]);

  // Validate rows whenever column mapping changes
  useEffect(() => {
    // Only skip validation if we have initialData AND we're not showing column mapping
    // (i.e., we're displaying initial data, not importing a new file)
    if (initialData && !showColumnMapping) {
      // Don't re-validate if we have initialData and we're not in column mapping mode
      return;
    }

    if (!fileContent || !showColumnMapping) {
      setValidRowCount(null);
      setValidatedRows([]);
      return;
    }

    try {
      // Parse CSV using parseCsv function
      const rows = parseCsv(fileContent, { minColumns: 1 });

      if (rows.length === 0) {
        setValidRowCount(0);
        setValidatedRows([]);
        return;
      }

      // Validate each row using parseCsvRowCoordinate
      const validRows: GcpRow[] = [];
      const seenNames = new Set<string>();
      let duplicateNamesCount = 0;
      let foundInvalidCoordinates = false;

      if (!coordinateSystem) {
        setValidRowCount(0);
        setValidatedRows([]);
        setShowDuplicateNamesWarning(false);
        setShowInvalidCoordinatesWarning(false);
        return;
      }

      for (const row of rows) {
        // Check if row has enough columns
        if (
          row.length <= columnMapping.northing ||
          row.length <= columnMapping.easting ||
          row.length <= columnMapping.height ||
          !row[columnMapping.northing] ||
          !row[columnMapping.easting] ||
          !row[columnMapping.height]
        ) {
          continue;
        }

        // Extract and check name for duplicates
        const nameValue = row[columnMapping.name]?.trim() || '';
        const nameKey = nameValue.toLowerCase();

        // Track duplicate names (check before coordinate parsing so we catch duplicates even if parsing fails)
        if (nameKey && seenNames.has(nameKey)) {
          duplicateNamesCount++;
          // Continue to try parsing coordinates to check for invalid coordinates warning
        } else if (nameKey) {
          // Add name to seenNames before parsing, so duplicates are detected even if parsing fails
          seenNames.add(nameKey);
        }

        // Try to parse coordinates to check validity
        try {
          const coordinate = parseCsvRowCoordinate(
            row,
            columnMapping.northing,
            columnMapping.easting,
            columnMapping.height,
            coordinateSystem,
            geoid,
          );

          // Name was already added to seenNames above if it exists

          // Convert to GcpRow format and save
          validRows.push({
            name: {
              status: nameValue === '' ? 'invalid' : 'valid',
              value: nameValue,
            },
            n: {
              status: 'valid_geoid',
              value: coordinate.northing.toString(),
            },
            e: {
              status: 'valid_geoid',
              value: coordinate.easting.toString(),
            },
            h: {
              status: 'valid_format',
              value: coordinate.height.toString(),
            },
            included: true,
          });
        } catch (err) {
          foundInvalidCoordinates = true;
        }
      }

      // Check for name duplications in valid rows
      const names = new Map<string, number>();
      validRows.forEach((row) => {
        const name = row.name.value.trim().toLowerCase();
        if (name) {
          names.set(name, (names.get(name) || 0) + 1);
        }
      });

      validRows.forEach((row) => {
        const name = row.name.value.trim().toLowerCase();
        if (names.get(name)! > 1) {
          row.name.status = 'duplication';
        }
      });

      // Update duplicate names warning
      setShowDuplicateNamesWarning(duplicateNamesCount > 0);
      setShowInvalidCoordinatesWarning(foundInvalidCoordinates);

      setValidRowCount(validRows.length);
      setValidatedRows(validRows);
    } catch (err) {
      console.error('Failed to validate GCP file:', err);
      setValidRowCount(0);
      setValidatedRows([]);
    }
  }, [fileContent, columnMapping, showColumnMapping, coordinateSystem, geoid, initialData]);

  const handleRowRemoved = React.useCallback(
    (index: number) => {
      // When imported, use accumulatedPoints; otherwise use validatedRows
      const sourceRows = isImported && !showColumnMapping ? accumulatedPoints : validatedRows;
      const newRows = sourceRows.filter((_, i) => i !== index);
      setValidatedRows(newRows);
      setAccumulatedPoints(newRows);
      setValidRowCount(newRows.length);

      // Notify parent of data change if callback is provided
      if (onDataChange && selectedFile) {
        onDataChange({
          filename: selectedFile.name,
          gcpPoints: newRows,
        });
      }
    },
    [validatedRows, accumulatedPoints, isImported, showColumnMapping, selectedFile, onDataChange],
  );

  const handleImport = React.useCallback(() => {
    if (!selectedFile || validatedRows.length === 0) {
      return;
    }

    setError(null);

    try {
      // Create a set of new point names (case-insensitive) for quick lookup
      const newPointNames = new Set<string>();
      validatedRows.forEach((point) => {
        const nameKey = point.name.value.trim().toLowerCase();
        if (nameKey) {
          newPointNames.add(nameKey);
        }
      });

      // Filter out existing points that have the same name as new points (they will be replaced)
      const existingPointsWithoutDuplicates = accumulatedPoints.filter((point) => {
        const nameKey = point.name.value.trim().toLowerCase();
        // Keep points that don't have a name or don't match any new point name
        return !nameKey || !newPointNames.has(nameKey);
      });

      // Combine: existing points (without duplicates) + all new points
      const allPoints = [...existingPointsWithoutDuplicates, ...validatedRows];

      // Update accumulated points
      setAccumulatedPoints(allPoints);

      // Call the callback with all data (existing + new)
      onImport({
        filename: selectedFile.name,
        gcpPoints: allPoints,
      });

      // After import, hide column mapping and show table
      setShowColumnMapping(false);
      setIsImported(true);

      // Clear file selection state for next import
      setSelectedFile(null);
      setFileContent(null);
      setCsvColumns([]);
      setValidatedRows([]);
      setValidRowCount(null);
      setShowDuplicateNamesWarning(false);
      setShowInvalidCoordinatesWarning(false);

      // Notify parent of data change if callback is provided
      if (onDataChange) {
        onDataChange({
          filename: selectedFile.name,
          gcpPoints: allPoints,
        });
      }
    } catch (err) {
      console.error('Failed to import GCP file:', err);
      setError(
        err instanceof Error
          ? err.message
          : t('geodataSetup.uploadGcp.failedToParse', { ns: 'cloudProcessing' }),
      );
    }
  }, [selectedFile, validatedRows, accumulatedPoints, onImport, onDataChange, t]);

  const handleCancel = React.useCallback(() => {
    setShowColumnMapping(false);
    setSelectedFile(null);
    setFileContent(null);
    setCsvColumns([]);
    setValidRowCount(null);
    setValidatedRows([]);
    setError(null);
    setShowDuplicateNamesWarning(false);
    setShowInvalidCoordinatesWarning(false);
    // If we had imported data, keep it; otherwise reset to initial state
    if (!isImported) {
      setIsImported(false);
    }
  }, [isImported]);

  const { getRootProps } = useDropzone({
    noClick: true, // Prevents file picker from being displayed when clicking on inner elements.
    accept: {
      'text/plain': ['.txt'],
      'text/csv': ['.csv'],
    },
    onDrop: React.useCallback(
      async (acceptedFiles: File[], rejectedFiles: any[]) => {
        dragCount.current = 0;
        setIsDropable(false);
        if (acceptedFiles.length > 0) {
          await handleFileSelected(acceptedFiles[0]);
        } else if (rejectedFiles.length > 0) {
          setError(t('geodataSetup.uploadGcp.unsupportedFileType', { ns: 'cloudProcessing' }));
        }
      },
      [handleFileSelected, t],
    ),
    onDragEnter: (e) => {
      e.preventDefault();
      dragCount.current++;
      setIsDropable(true);
    },
    onDragLeave: (e) => {
      dragCount.current--;
      if (dragCount.current === 0) {
        setIsDropable(false);
      }
    },
  });

  const selectFilesButton = (
    <FileSelectionButton
      accept=".txt,.csv"
      color="secondary"
      leftIcon={{ icon: ['fad', 'upload'] }}
      multiple={false}
      text={t('fileManager.uploadFilesDialog.chooseFilesButton', { ns: 'components' })}
      variant="contained"
      onFilesSelected={(files: File[]) => {
        if (files.length > 0) {
          void handleFileSelected(files[0]);
        }
      }}
    />
  );

  // Table columns definition
  type TableItem = {
    statusIcon: React.ReactNode;
    name: string;
    n: string;
    e: string;
    h: string;
    actions: React.ReactNode;
    originalRow: GcpRow;
  };

  const shouldShowActions =
    (isImported && !showColumnMapping ? accumulatedPoints : validatedRows).length > 0;

  const tableColumns = useMemo<DataTableColumns<TableItem>>(
    () => ({
      statusIcon: {
        title: t('geodataSetup.uploadGcp.columns.status', { ns: 'cloudProcessing' }),
        alignment: 'center',
        width: '60px',
        unfilterable: true,
        render: (item: TableItem) => item.statusIcon,
      },
      name: {
        title: t('geodataSetup.uploadGcp.columns.name', { ns: 'cloudProcessing' }),
        sortableBy: { type: DataTableSortType.STRING },
        unfilterable: true,
        alignment: 'left',
      },
      n: {
        title: t('geodataSetup.uploadGcp.columns.n', { ns: 'cloudProcessing' }),
        alignment: 'left',
        sortableBy: { type: DataTableSortType.STRING },
        unfilterable: true,
      },
      e: {
        title: t('geodataSetup.uploadGcp.columns.e', { ns: 'cloudProcessing' }),
        alignment: 'left',
        sortableBy: { type: DataTableSortType.STRING },
        unfilterable: true,
      },
      h: {
        title: t('geodataSetup.uploadGcp.columns.h', { ns: 'cloudProcessing' }),
        alignment: 'left',
        sortableBy: { type: DataTableSortType.STRING },
        unfilterable: true,
      },
      ...(shouldShowActions && {
        actions: {
          title: '',
          alignment: 'center',
          width: '60px',
          unfilterable: true,
          render: (item: TableItem) => item.actions,
        },
      }),
    }),
    [t, shouldShowActions],
  );

  const tableItems = useMemo<TableItem[]>(() => {
    // Use accumulatedPoints for display when imported, otherwise use validatedRows (for preview during column mapping)
    const rowsToDisplay = isImported && !showColumnMapping ? accumulatedPoints : validatedRows;
    return rowsToDisplay.map((row, index) => {
      const isValid = !isInvalidGcpRow(row);
      return {
        originalRow: row,
        name: row.name.value,
        n: row.n.value,
        e: row.e.value,
        h: row.h.value,
        statusIcon: (
          <Icon
            color={isValid ? theme.color.green : theme.color.red}
            icon={isValid ? ['fas', 'check-circle'] : ['fas', 'exclamation-circle']}
            title={
              isValid
                ? t('geodataSetup.uploadGcp.valid', { ns: 'cloudProcessing' })
                : t('geodataSetup.uploadGcp.invalid', { ns: 'cloudProcessing' })
            }
          />
        ),
        actions: (
          <Icon
            color={theme.color.gray.medium}
            icon={['fas', 'trash-alt']}
            style={{ cursor: 'pointer' }}
            title={t('geodataSetup.uploadGcp.removePoint', { ns: 'cloudProcessing' })}
            onClick={() => handleRowRemoved(index)}
          />
        ),
      };
    });
  }, [validatedRows, theme, handleRowRemoved, t, accumulatedPoints, isImported, showColumnMapping]);

  // Show dropzone when: no points added OR dragging new files
  const showDropzone = !showColumnMapping && (accumulatedPoints.length === 0 || isDropable);
  // Show table when: points are added AND not dragging new files
  const showTableWithPoints = !showColumnMapping && accumulatedPoints.length > 0 && !isDropable;

  const titleText = useMemo(() => {
    return `${t('geodataSetup.uploadGcp.heading', { ns: 'cloudProcessing' })} (${accumulatedPoints.length})`;
  }, [accumulatedPoints.length, t]);

  return (
    <Container>
      <Content {...getRootProps()}>
        {/* Show dropzone when no points or when dragging */}
        {showDropzone && (
          <>
            <h4>{titleText}</h4>
            <DragAndDropInfo>
              <IconPanel
                header={{ icon: { icon: ['fad', 'list-ul'], opacity: '50%', size: '5x' } }}
                responsiveness={{ compressed: false }}
              >
                {{
                  title: (
                    <Stack>
                      <span>
                        {t('geodataSetup.uploadGcp.addGcpPointsTitle', { ns: 'cloudProcessing' })}
                      </span>
                      <span>({t('optional', { ns: 'common' })})</span>
                    </Stack>
                  ),
                  info: (
                    <Trans
                      components={{ selectFilesButton: selectFilesButton }}
                      i18nKey="geodataSetup.dragAndDropInfo"
                      ns="cloudProcessing"
                    />
                  ),
                }}
              </IconPanel>
            </DragAndDropInfo>
          </>
        )}

        {/* Show table when points are added and not dragging */}
        {showTableWithPoints && (
          <ImportedViewContainer>
            <Stack alignItems="center" direction="row" justifyContent="space-between">
              <h4>{titleText}</h4>
              {selectFilesButton}
            </Stack>
            <TableContainer>
              <DataTable
                columns={tableColumns}
                fixedLayout={true}
                items={tableItems}
                pageSize={10}
                tableStyle={{
                  rowCell: {
                    fontSize: '14px',
                  },
                  headerCell: {
                    fontSize: '14px',
                  },
                }}
              />
            </TableContainer>
          </ImportedViewContainer>
        )}

        {showColumnMapping && (
          <GcpColumnMappingComponent
            accumulatedPointsCount={accumulatedPoints.length}
            columnMapping={columnMapping}
            csvColumns={csvColumns}
            projectionRange={projectionRange}
            showDuplicateNamesWarning={showDuplicateNamesWarning}
            showInvalidCoordinatesWarning={showInvalidCoordinatesWarning}
            validRowCount={validRowCount}
            onCancel={handleCancel}
            onColumnMappingChange={setColumnMapping}
            onImport={handleImport}
          />
        )}

        {error && (
          <ErrorInfoBox>
            <InfoBox color="red">{error}</InfoBox>
          </ErrorInfoBox>
        )}
      </Content>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const Content = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const DragAndDropInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed #000;
  padding: 2em;
  height: 100%;
  width: 100%;

  button {
    margin: 0.75em auto 0;
  }
`;

const ErrorInfoBox = styled.div`
  margin-top: 1rem;
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: hidden;
  min-height: 0;
  width: 100%;
  margin-top: 1rem;

  /* Prevent horizontal scrolling */
  > * {
    max-width: 100%;
    overflow-x: hidden;
  }
`;

const ImportedViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
`;
